import React, { useState, useEffect } from 'react';
import {
  Search, Trash2, RefreshCw, AlertCircle, Filter,
  ChevronLeft, ChevronRight, MessageCircle
} from 'lucide-react';

import MessageList from './MessageList';
import MessageDetails from './MessageDetails';
import DeleteConfirmation from './DeleteConfirmation';
import StatsOverview from './StatsOverview';
import LoadingState from './LoadingState';
import { fetchMessages, deleteMessage, markAsRead } from './messageService';
import './styles.css';

const ManageMessage = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    if (messages.length) {
      let filtered = [...messages];

      if (searchTerm) {
        filtered = filtered.filter(
          message =>
            message.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            message.message.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (filterStatus !== 'all') {
        filtered = filtered.filter(message =>
          filterStatus === 'read' ? message.read : !message.read
        );
      }

      setFilteredMessages(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, messages, filterStatus]);

  const loadMessages = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchMessages();
      setMessages(data);
      setFilteredMessages(data);
    } catch (err) {
      setError('Failed to load messages. Please try again.');
      console.error('Error fetching messages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      await deleteMessage(deleteId);
      setMessages(messages.filter(message => message.id !== deleteId));
      setFilteredMessages(filteredMessages.filter(message => message.id !== deleteId));
      if (selectedMessage?.id === deleteId) {
        setSelectedMessage(null);
      }
    } catch (err) {
      setError('Failed to delete message. Please try again.');
      console.error('Error deleting message:', err);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleViewMessage = async (message) => {
    if (!message.read) {
      try {
        await markAsRead(message.id);
        const updatedMessages = messages.map(m =>
          m.id === message.id ? { ...m, read: true } : m
        );
        setMessages(updatedMessages);
        setFilteredMessages(
          filteredMessages.map(m => m.id === message.id ? { ...m, read: true } : m)
        );
      } catch (err) {
        console.error('Error marking message as read:', err);
      }
    }
    setSelectedMessage(message);
  };

  const handleCloseDetails = () => {
    setSelectedMessage(null);
  };

  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleRefresh = () => loadMessages();

  if (error && !isLoading) {
    return (
    
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
        <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 dark:text-red-400 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Something went wrong</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
            <button
              onClick={handleRefresh}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Try Again
            </button>
          </div>
        </div>
      </div>
  
    );
  }

  return (
 
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-2xl font-semibold flex items-center text-gray-800 dark:text-white">
            <MessageCircle className="w-6 h-6 mr-2" />
            Message Management
          </h1>
        </div>

        {!isLoading && (
          <StatsOverview
            totalMessages={messages.length}
            unreadCount={messages.filter(m => !m.read).length}
          />
        )}

        <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search messages..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Messages</option>
                  <option value="read">Read</option>
                  <option value="unread">Unread</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-400">
                  <Filter className="h-4 w-4" />
                </div>
              </div>

              <button
                onClick={handleRefresh}
                className="flex items-center justify-center p-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row h-[calc(100vh-320px)] min-h-[400px]">
          <div className={`w-full ${selectedMessage ? 'hidden md:block md:w-1/3' : 'md:w-full'} border-r border-gray-200 dark:border-gray-700 overflow-y-auto`}>
            {isLoading ? (
              <LoadingState />
            ) : filteredMessages.length > 0 ? (
              <>
                <MessageList
                  messages={currentMessages}
                  onView={handleViewMessage}
                  onDelete={handleDeleteClick}
                  selectedId={selectedMessage?.id}
                />
                {/* Pagination buttons can be left unchanged or styled similarly */}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
                <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-1">No messages found</h2>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Your inbox is empty'}
                </p>
                {(searchTerm || filterStatus !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {selectedMessage && (
            <div className="w-full md:w-2/3 flex flex-col h-full">
              <MessageDetails
                message={selectedMessage}
                onClose={handleCloseDetails}
                onDelete={() => handleDeleteClick(selectedMessage.id)}
              />
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmation
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isLoading={isLoading}
      />
    </div>
  
  );
};

export default ManageMessage;
